import { useField } from 'formik';
import { MouseEventHandler, memo } from 'react';
import { useLatest } from 'react-use';

import {
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    SelectProps,
} from '@mui/material';

import { InterviewLanguage, Question, QuestionType } from '@synergetx/puppet-interview-types';

import { removeKeysAfterQuestionTypeChange } from './CreateSurveyUtils';
import QuestionTypeSpecificSettings from './QuestionTypeSpecificSettings';

export const QUESTION_TYPE_READABLE_STRINGS: {
    [key in QuestionType]: string;
} = {
    [QuestionType.OPEN]: 'Short Response',
    [QuestionType.PUPPET]: 'Puppet',
    [QuestionType.INFO]: 'Note',
    [QuestionType.DATE]: 'Date input',
    [QuestionType.NUMBER]: 'Number input',
    [QuestionType.MULTIPLE]: 'Multiple/Single Choice',
    [QuestionType.CONTINUE]: 'Continue Interview Prompt',
};

interface Props {
    position: number;
    value: QuestionType | undefined;
}

const QuestionTypeSelect = ({ position, value }: Props) => {
    const [{ value: question }, , { setValue: setQuestion }] = useField<Question>(
        `questions[${position}]`,
    );
    const [{ value: languages }] = useField<InterviewLanguage[]>(`languages`);

    const handleChangeType: SelectProps<QuestionType>['onChange'] = useLatest(
        ({ target }: SelectChangeEvent<QuestionType>): void => {
            setQuestion(
                removeKeysAfterQuestionTypeChange(
                    question,
                    languages,
                    target.value as QuestionType,
                ),
            );
        },
    ).current;

    return (
        <>
            <Grid size={6} sx={{ paddingTop: 1 }}>
                <FormControl>
                    <InputLabel id="question-type-label">{'Question Type'}</InputLabel>
                    <Select
                        id="question-type"
                        labelId="question-type-label"
                        label="Question Type"
                        value={value}
                        fullWidth
                        onChange={handleChangeType}
                        onClick={onClick}
                        sx={{ '& .MuiSelect-select': { minWidth: '15rem' } }}
                    >
                        {Object.values(QuestionType).map((type) => (
                            <MenuItem key={type} value={type}>
                                {QUESTION_TYPE_READABLE_STRINGS[type]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <QuestionTypeSpecificSettings position={position} />
        </>
    );
};

const onClick: MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
};

export default memo(QuestionTypeSelect);
